<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useRoute } from 'vue-router'
import { type Client, clientsApi } from '@/api/entities/review/clients'
import { type DriverConfig, driverConfigsApi } from '@/api/entities/review/driverConfigs'

definePage({
  meta: {
    title: '정기이관 업체별 관리',
    action: 'read',
    subject: 'review',
  },
})

const route = useRoute()

const { snackbar, handleError } = useErrorHandler()

// ISO 날짜를 YYYY-MM-DD 형식으로 변환
const formatDateForInput = (isoDate: string | null): string | null => {
  if (!isoDate)
    return null
  try {
    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime()))
      return null

    return date.toISOString().split('T')[0]
  }
  catch {
    return null
  }
}

// 로컬 인터페이스
interface DriverConfigRow {
  id: number
  clientId: number
  driver: string
  product_count: number
  monthly_state: boolean
  start: string | null
  end: string | null
  saving?: boolean
}

// 상태
const clientsLoading = ref(false)
const clients = ref<Client[]>([])
const totalActiveClients = ref(0)

// 필터
const clientFilter = ref('')
const onlyActiveFilter = ref(false)
const onlyExpiredFilter = ref(false)
const perPage = ref(100)
const perPageOptions = [50, 100, 200, { title: '전체', value: -1 }]

// 펼침 상태 관리
const expandedClients = ref<number[]>([])
const isExpandingAll = ref(false)

// Map/Set 대신 reactive object 사용 (Vue 반응성 보장)
const clientConfigs = reactive<Record<number, DriverConfigRow[]>>({})
const loadingClients = reactive<Record<number, boolean>>({})

// 서버에서 클라이언트 목록 로드 (AJAX 필터링)
const loadClients = async () => {
  clientsLoading.value = true

  try {
    const params: Record<string, unknown> = {
      per_page: perPage.value,
      type: 'migration',
    }

    // 검색어 필터
    if (clientFilter.value.trim())
      params.query = clientFilter.value.trim()

    // 정기 이관 활성 필터
    if (onlyActiveFilter.value)
      params.has_monthly = 'true'

    // 기간 만료 업체 필터
    if (onlyExpiredFilter.value)
      params.has_expired_monthly = 'true'

    const { success, data } = await clientsApi.listClients(params)

    if (success && data)
      clients.value = data.data || []
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    clientsLoading.value = false
  }
}

// 정기 이관 활성 고객사 수 로드 (별도 API 호출)
const loadActiveCount = async () => {
  try {
    const { success, data } = await clientsApi.listClients({
      per_page: 1,
      type: 'migration',
      has_monthly: 'true',
    })

    if (success && data)
      totalActiveClients.value = data.meta?.total || 0
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// Debounced 검색 (300ms)
const debouncedSearch = useDebounceFn(() => {
  loadClients()
}, 300)

// 클라이언트별 드라이버 설정 로드
const loadDriverConfigs = async (clientId: number) => {
  if (loadingClients[clientId])
    return

  loadingClients[clientId] = true

  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(clientId, {
      per_page: 100,
    })

    if (success && data) {
      const configs = (data.data || []).map((config: DriverConfig) => ({
        id: config.id,
        clientId,
        driver: config.download_driver?.name || `드라이버 ${config.id}`,
        product_count: config.target_item_maps_count || 0,
        monthly_state: config.monthly_state === 'Y',
        start: formatDateForInput(config.auto_migration_start_date),
        end: formatDateForInput(config.auto_migration_end_date),
        saving: false,
      }))

      clientConfigs[clientId] = configs
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loadingClients[clientId] = false
  }
}

// 클라이언트 드라이버 설정 가져오기
const getClientConfigs = (clientId: number): DriverConfigRow[] => {
  return clientConfigs[clientId] || []
}

// 클라이언트 로딩 중인지 확인
const isClientLoading = (clientId: number): boolean => {
  return !!loadingClients[clientId]
}

// 저장 가능 여부 확인
const ableToSave = (item: DriverConfigRow) => {
  if (item.monthly_state)
    return item.start && item.end

  return true
}

// 설정 저장
const save = async (item: DriverConfigRow) => {
  item.saving = true

  const payload = {
    monthly_state: item.monthly_state ? 'Y' : 'N',
    auto_migration_start_date: item.start,
    auto_migration_end_date: item.end,
  }

  try {
    const { success, error } = await driverConfigsApi.updateDriverConfig(
      item.clientId,
      item.id,
      payload,
    )

    if (success) {
      snackbar.success('정기 이관 설정 저장에 성공했습니다.')
      await loadActiveCount()
      await loadClients()

      // 해당 클라이언트의 캐시 갱신
      delete clientConfigs[item.clientId]
      await loadDriverConfigs(item.clientId)
    }
    else {
      const errorMessage = error?.message || '알 수 없는 오류'

      snackbar.error(`정기 이관 설정 저장에 실패했습니다: ${errorMessage}`)
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '정기 이관 설정 저장에 실패했습니다.' })
  }
  finally {
    item.saving = false
  }
}

// 행 스타일
const getConfigRowClass = (item: DriverConfigRow) => {
  if (item.monthly_state)
    return 'bg-success-lighten'

  return ''
}

// 클라이언트 상태 아이콘
const getClientIcon = (client: Client) => {
  if (client.has_monthly_config && client.has_expired_monthly_config)
    return { icon: 'tabler-alert-circle-filled', color: 'warning' }

  if (client.has_monthly_config)
    return { icon: 'tabler-circle-check-filled', color: 'success' }

  return { icon: 'tabler-circle', color: 'grey-lighten-1' }
}

// 펼침 상태 감시 - 새로 펼쳐진 클라이언트의 설정 로드
// isExpandingAll이 true일 때는 건너뜀 (배치 처리로 별도 로드)
watch(expandedClients, (newVal, oldVal) => {
  if (isExpandingAll.value)
    return

  const newlyExpanded = newVal.filter(id => !oldVal?.includes(id))
  for (const clientId of newlyExpanded) {
    if (!clientConfigs[clientId])
      loadDriverConfigs(clientId)
  }
}, { deep: true })

// 검색어 변경 감시 (debounce)
watch(clientFilter, () => {
  debouncedSearch()
})

// 활성 필터 변경 감시 (즉시 적용)
watch(onlyActiveFilter, () => {
  loadClients()
})

// 만료 필터 변경 감시 (즉시 적용)
watch(onlyExpiredFilter, () => {
  loadClients()
})

// 표시 개수 변경 감시 (즉시 적용)
watch(perPage, () => {
  loadClients()
})

// 전체 펼치기/접기
const isAllExpanded = computed(() => {
  return clients.value.length > 0 && expandedClients.value.length === clients.value.length
})

// 배치로 드라이버 설정 로드 (동시 요청 수 제한)
const loadDriverConfigsBatch = async (clientIds: number[], batchSize = 5) => {
  for (let i = 0; i < clientIds.length; i += batchSize) {
    const batch = clientIds.slice(i, i + batchSize)

    await Promise.all(
      batch
        .filter(id => !clientConfigs[id])
        .map(id => loadDriverConfigs(id)),
    )
  }
}

const toggleAll = async () => {
  if (isAllExpanded.value) {
    expandedClients.value = []
  }
  else {
    isExpandingAll.value = true

    const allIds = clients.value.map(c => c.id)

    expandedClients.value = allIds

    // 배치로 로드 (5개씩)
    await loadDriverConfigsBatch(allIds, 5)
    isExpandingAll.value = false
  }
}

// Query parameter에서 client_id로 클라이언트 펼치기
const expandFromQuery = async () => {
  const clientId = route.query.client_id

  if (!clientId)
    return

  const id = Number(clientId)
  if (Number.isNaN(id))
    return

  // 클라이언트 목록에 있는지 확인
  const client = clients.value.find(c => c.id === id)

  if (client) {
    // 해당 클라이언트 펼치기
    if (!expandedClients.value.includes(id))
      expandedClients.value = [...expandedClients.value, id]
  }
  else {
    // 목록에 없으면 API로 직접 조회 후 목록에 추가
    try {
      const { success, data } = await clientsApi.getClient(id)

      if (success && data) {
        clients.value = [data as Client, ...clients.value]
        expandedClients.value = [id]
      }
    }
    catch (error) {
      handleError(error, { silent: true })
    }
  }
}

onMounted(async () => {
  await loadClients()
  loadActiveCount()

  // Query parameter 처리
  await expandFromQuery()
})
</script>

<template>
  <VContainer fluid>
    <!-- 상단 필터 및 통계 -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="3"
          >
            <VTextField
              v-model="clientFilter"
              density="compact"
              variant="outlined"
              placeholder="고객사 검색..."
              prepend-inner-icon="tabler-search"
              hide-details
              clearable
              :loading="clientsLoading"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VSwitch
              v-model="onlyActiveFilter"
              density="compact"
              hide-details
              color="success"
              label="활성만 보기"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VSwitch
              v-model="onlyExpiredFilter"
              density="compact"
              hide-details
              color="warning"
              label="만료만 보기"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VSelect
              v-model="perPage"
              :items="perPageOptions"
              label="표시 개수"
              density="compact"
              hide-details
              variant="outlined"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VBtn
              variant="outlined"
              size="small"
              :loading="isExpandingAll"
              :disabled="isExpandingAll"
              @click="toggleAll"
            >
              <VIcon
                start
                :icon="isAllExpanded ? 'tabler-fold' : 'tabler-fold-down'"
              />
              {{ isAllExpanded ? '전체 접기' : '전체 펼치기' }}
            </VBtn>
          </VCol>
          <VCol
            cols="12"
            md="3"
            class="d-flex justify-end gap-4"
          >
            <VChip
              color="primary"
              variant="tonal"
            >
              검색 결과: {{ clients.length }}개
            </VChip>
            <VChip
              color="success"
              variant="tonal"
            >
              활성 업체: {{ totalActiveClients }}개
            </VChip>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- 고객사 펼침 목록 -->
    <VCard>
      <VCardText class="pa-0">
        <div
          v-if="clientsLoading"
          class="text-center py-8"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
          <p class="mt-2 text-medium-emphasis">
            로딩 중...
          </p>
        </div>

        <div
          v-else-if="clients.length === 0"
          class="text-center py-8 text-grey"
        >
          검색 결과가 없습니다.
        </div>

        <VExpansionPanels
          v-else
          v-model="expandedClients"
          multiple
        >
          <VExpansionPanel
            v-for="client in clients"
            :key="client.id"
            :value="client.id"
          >
            <VExpansionPanelTitle>
              <div class="d-flex align-center gap-2 w-100">
                <!-- 상태 아이콘 -->
                <VTooltip
                  v-if="client.has_monthly_config && client.has_expired_monthly_config"
                  location="top"
                >
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      :icon="getClientIcon(client).icon"
                      :color="getClientIcon(client).color"
                      size="20"
                    />
                  </template>
                  <span>정기 이관 기간이 만료되었습니다</span>
                </VTooltip>
                <VIcon
                  v-else
                  :icon="getClientIcon(client).icon"
                  :color="getClientIcon(client).color"
                  size="20"
                />

                <!-- 고객사명 -->
                <span class="font-weight-medium">{{ client.name }}</span>

                <!-- 채널 수 표시 -->
                <VChip
                  v-if="clientConfigs[client.id]"
                  size="x-small"
                  color="secondary"
                  variant="tonal"
                  class="ms-2"
                >
                  {{ getClientConfigs(client.id).length }}개 채널
                </VChip>
              </div>
            </VExpansionPanelTitle>

            <VExpansionPanelText>
              <!-- 위젯 유형 안내 -->
              <div
                v-if="client.type !== 'migration'"
                class="text-center py-4 text-medium-emphasis"
              >
                위젯 유형의 고객사는 정기 이관 설정을 할 수 없습니다.
              </div>

              <!-- 로딩 -->
              <div
                v-else-if="isClientLoading(client.id)"
                class="text-center py-4"
              >
                <VProgressCircular
                  indeterminate
                  size="24"
                  color="primary"
                />
                <span class="ms-2 text-medium-emphasis">채널 정보 로딩 중...</span>
              </div>

              <!-- 채널 목록 없음 -->
              <div
                v-else-if="getClientConfigs(client.id).length === 0"
                class="text-center py-4 text-medium-emphasis"
              >
                등록된 채널이 없습니다.
              </div>

              <!-- 채널 목록 -->
              <VTable
                v-else
                density="compact"
              >
                <thead>
                  <tr>
                    <th style="width: 60px">
                      ID
                    </th>
                    <th>채널</th>
                    <th style="width: 100px">
                      매핑 제품 수
                    </th>
                    <th style="width: 80px">
                      정기 이관
                    </th>
                    <th style="width: 160px">
                      시작일
                    </th>
                    <th style="width: 160px">
                      종료일
                    </th>
                    <th style="width: 80px" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="config in getClientConfigs(client.id)"
                    :key="config.id"
                    :class="getConfigRowClass(config)"
                  >
                    <td>{{ config.id }}</td>
                    <td>{{ config.driver }}</td>
                    <td class="text-end">
                      {{ config.product_count }}
                    </td>
                    <td>
                      <VSwitch
                        v-model="config.monthly_state"
                        hide-details
                        density="compact"
                        color="success"
                      />
                    </td>
                    <td>
                      <VTextField
                        v-model="config.start"
                        type="date"
                        density="compact"
                        hide-details
                        :disabled="!config.monthly_state"
                        variant="outlined"
                      />
                    </td>
                    <td>
                      <VTextField
                        v-model="config.end"
                        type="date"
                        density="compact"
                        hide-details
                        :disabled="!config.monthly_state"
                        :min="config.start"
                        variant="outlined"
                      />
                    </td>
                    <td>
                      <VBtn
                        variant="outlined"
                        color="primary"
                        size="small"
                        :disabled="!ableToSave(config)"
                        :loading="config.saving"
                        @click="save(config)"
                      >
                        저장
                      </VBtn>
                    </td>
                  </tr>
                </tbody>
              </VTable>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>
    </VCard>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VContainer>
</template>

<style scoped>
.bg-success-lighten {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}
</style>
